import React, { useState, useEffect } from 'react';
import { theme } from '../../theme';
import { CATEGORIES, PRICE_UNITS } from '../../constants';
import { formatPrice, formatDays } from '../../utils/formatters';
import { useApi } from '../../hooks/useApi';

export function MakerProfileScreen({ makerId, onNavigate }) {
  const api = useApi();
  const [maker, setMaker] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!makerId) { setLoading(false); return; }
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const res = await api.makers.get(makerId);
        if (!cancelled) {
          const data = res.data || res;
          setMaker(data);
          setServices(data.services || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [makerId]);

  if (loading) {
    return <div style={{ padding: '16px', textAlign: 'center', color: theme.textMuted }}>Hleð...</div>;
  }

  if (error || !maker) {
    return (
      <div style={{ padding: '16px' }}>
        <button
          onClick={() => onNavigate('browse')}
          style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
        >
          ← Til baka
        </button>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>🔧</p>
          <p style={{ color: theme.text, fontWeight: 600 }}>{error || 'Maker finnst ekki'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <button
        onClick={() => onNavigate('browse')}
        style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
      >
        ← Til baka
      </button>

      {/* Maker Header */}
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        padding: '20px',
        textAlign: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{maker.avatar || '🔧'}</div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: theme.text }}>{maker.name}</h2>
        {maker.location && (
          <p style={{ fontSize: '13px', color: theme.textSecondary, marginTop: '4px' }}>
            📍 {maker.location}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '12px' }}>
          <div>
            <span style={{ fontSize: '18px', fontWeight: 700, color: theme.accent }}>
              {'★'.repeat(Math.round(maker.rating || 0))}
            </span>
            <span style={{ fontSize: '13px', color: theme.textMuted, marginLeft: '4px' }}>
              {(maker.rating || 0).toFixed(1)}
            </span>
          </div>
          <div style={{ fontSize: '13px', color: theme.textSecondary }}>
            {maker.completed_orders || 0} pantanir
          </div>
        </div>
        {maker.bio && (
          <p style={{ fontSize: '14px', color: theme.textSecondary, marginTop: '12px', lineHeight: 1.5 }}>
            {maker.bio}
          </p>
        )}
        {maker.phone && (
          <a
            href={`tel:${maker.phone}`}
            style={{
              display: 'inline-block',
              marginTop: '12px',
              padding: '10px 20px',
              background: theme.accentBg,
              border: `1px solid ${theme.accent}`,
              borderRadius: theme.radiusSm,
              color: theme.accent,
              fontWeight: 600,
              fontSize: '14px',
              textDecoration: 'none',
            }}
          >
            📞 Hringja
          </a>
        )}
      </div>

      {/* Services */}
      <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.text, marginBottom: '12px' }}>
        Þjónustur ({services.length})
      </h3>
      {services.length === 0 ? (
        <div style={{
          background: theme.bgCard,
          border: `1px solid ${theme.border}`,
          borderRadius: theme.radius,
          padding: '24px',
          textAlign: 'center',
        }}>
          <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
            Engar þjónustur skráðar enn
          </p>
        </div>
      ) : (
        services.map(svc => {
          const cat = CATEGORIES.find(c => c.id === svc.category);
          return (
            <div key={svc.id} style={{
              background: theme.bgCard,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              padding: '16px',
              marginBottom: '8px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <span style={{ fontSize: '12px', color: theme.categories[svc.category] || theme.textMuted }}>
                    {cat?.icon} {cat?.label}
                  </span>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: theme.text, marginTop: '4px' }}>
                    {svc.title}
                  </h4>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: theme.accent }}>
                  {svc.base_price ? `${formatPrice(svc.base_price)} ${PRICE_UNITS[svc.price_unit] || ''}` : 'Samkomulag'}
                </span>
              </div>
              {svc.description && (
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginTop: '8px' }}>
                  {svc.description}
                </p>
              )}
              {svc.turnaround_days && (
                <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
                  ⏱ {formatDays(svc.turnaround_days)}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
